import { z } from "zod";
import { propertyTypeSchema } from "./property-schemas";
import { catalogServiceSchema } from "./schemas";

export const jobIdSchema = z.string().regex(/^[0-9a-f]{32}$/u);
export const jobBookingModeSchema = z.enum(["FIXED_ACCEPT", "NEGOTIABLE"]);
export const jobStatusSchema = z.enum([
  "DRAFT",
  "OPEN",
  "OFFERED",
  "BOOKED",
  "IN_PROGRESS",
  "COMPLETED",
  "CANCELLED",
]);
const fixedJobStatusSchema = z.enum([
  "DRAFT",
  "OPEN",
  "BOOKED",
  "IN_PROGRESS",
  "COMPLETED",
  "CANCELLED",
]);
export const cleaningTypeSchema = z.enum([
  "STANDARD",
  "DEEP",
  "MOVE_IN",
  "MOVE_OUT",
  "TURNOVER",
  "CUSTOM",
]);
export const jobPricingUnitSchema = catalogServiceSchema.shape.pricingUnit;

const isoDateTimeSchema = z.string().datetime({ offset: true });
const versionNoSchema = z.number().int().min(1);
const bedroomCountSchema = z.number().int().min(0);
const bathroomCountSchema = z.number().min(0).multipleOf(0.5);
const nullableRoomCountSchema = z.number().int().min(0).max(9_999).nullable();
const fixedPriceSchema = z.number().int().min(100).max(1_000_000);

function isCalendarDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value);
  if (match === null) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}

export const requestedDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/u, "Choose a date")
  .refine(isCalendarDate, "Choose a valid date");
export const localTimeSchema = z
  .string()
  .regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/u, "Choose a valid time");

export const jobServiceInputSchema = z.strictObject({
  serviceId: jobIdSchema,
  quantity: z.number().int().min(1).max(100),
});

const jobServicesInputSchema = z
  .array(jobServiceInputSchema)
  .min(1, "Choose at least one service")
  .max(100)
  .superRefine((services, context) => {
    const serviceIds = new Set<string>();
    services.forEach(({ serviceId }, index) => {
      if (serviceIds.has(serviceId)) {
        context.addIssue({
          code: "custom",
          path: [index, "serviceId"],
          message: "Choose each service only once",
        });
      }
      serviceIds.add(serviceId);
    });
  });

const mutableJobFields = {
  cleaningType: cleaningTypeSchema,
  requestedDate: requestedDateSchema,
  requestedStartTime: localTimeSchema,
  requestedEndTime: localTimeSchema,
  specialInstructions: z
    .union([z.string(), z.null()])
    .optional()
    .transform((value) => {
      const trimmed = value?.trim() ?? "";
      return trimmed === "" ? null : trimmed;
    })
    .refine((value) => value === null || value.length <= 4_000, "Use no more than 4000 characters"),
  needsLinen: z.boolean(),
  linenSetCount: z.number().int().min(0).max(9_999),
  needsTowels: z.boolean(),
  towelSetCount: z.number().int().min(0).max(9_999),
} as const;

function addMutableFieldIssues(
  value: {
    requestedStartTime: string;
    requestedEndTime: string;
    needsLinen: boolean;
    linenSetCount: number;
    needsTowels: boolean;
    towelSetCount: number;
  },
  context: z.RefinementCtx,
): void {
  if (value.requestedEndTime <= value.requestedStartTime) {
    context.addIssue({
      code: "custom",
      path: ["requestedEndTime"],
      message: "End time must be after start time",
    });
  }
  if (!value.needsLinen && value.linenSetCount !== 0) {
    context.addIssue({
      code: "custom",
      path: ["linenSetCount"],
      message: "Linen set count must be 0 when linen is not needed",
    });
  }
  if (value.needsLinen && value.linenSetCount < 1) {
    context.addIssue({
      code: "custom",
      path: ["linenSetCount"],
      message: "Enter at least one linen set",
    });
  }
  if (!value.needsTowels && value.towelSetCount !== 0) {
    context.addIssue({
      code: "custom",
      path: ["towelSetCount"],
      message: "Towel set count must be 0 when towels are not needed",
    });
  }
  if (value.needsTowels && value.towelSetCount < 1) {
    context.addIssue({
      code: "custom",
      path: ["towelSetCount"],
      message: "Enter at least one towel set",
    });
  }
}

const fixedCreateJobInputSchema = z.strictObject({
  propertyId: jobIdSchema,
  bookingMode: z.literal("FIXED_ACCEPT"),
  fixedPriceAmountMinor: fixedPriceSchema,
  startingPriceAmountMinor: z.null(),
  ...mutableJobFields,
  services: jobServicesInputSchema,
});
const negotiableCreateJobInputSchema = z.strictObject({
  propertyId: jobIdSchema,
  bookingMode: z.literal("NEGOTIABLE"),
  fixedPriceAmountMinor: z.null(),
  startingPriceAmountMinor: fixedPriceSchema,
  ...mutableJobFields,
  services: jobServicesInputSchema,
});
export const createJobInputSchema = z
  .discriminatedUnion("bookingMode", [fixedCreateJobInputSchema, negotiableCreateJobInputSchema])
  .superRefine(addMutableFieldIssues);

const fixedUpdateJobInputSchema = z.strictObject({
  versionNo: versionNoSchema,
  bookingMode: z.literal("FIXED_ACCEPT"),
  fixedPriceAmountMinor: fixedPriceSchema,
  startingPriceAmountMinor: z.null(),
  ...mutableJobFields,
});
const negotiableUpdateJobInputSchema = z.strictObject({
  versionNo: versionNoSchema,
  bookingMode: z.literal("NEGOTIABLE"),
  fixedPriceAmountMinor: z.null(),
  startingPriceAmountMinor: fixedPriceSchema,
  ...mutableJobFields,
});
export const updateJobInputSchema = z
  .discriminatedUnion("bookingMode", [fixedUpdateJobInputSchema, negotiableUpdateJobInputSchema])
  .superRefine(addMutableFieldIssues);

export const replaceJobServicesInputSchema = z.strictObject({
  versionNo: versionNoSchema,
  services: jobServicesInputSchema,
});

export const publishJobInputSchema = z.strictObject({
  versionNo: versionNoSchema,
});

// This is intentionally a version-only command. Customer cancellation does not collect a reason.
export const cancelJobInputSchema = z.strictObject({
  jobVersionNo: versionNoSchema,
});

const jobSummaryCommonFields = {
  id: jobIdSchema,
  jobNumber: z.string().regex(/^WBJ-[0-9A-F]{26}$/u),
  propertyId: jobIdSchema,
  propertyName: z.string().min(1),
  propertyType: propertyTypeSchema,
  currency: z.literal("USD"),
  requestStartAt: isoDateTimeSchema,
  requestEndAt: isoDateTimeSchema,
  timezoneRegion: z.string().min(1),
  estimatedDurationMinutes: z.number().int().min(1),
  cleaningType: cleaningTypeSchema,
  versionNo: versionNoSchema,
  createdAt: isoDateTimeSchema,
  postedAt: isoDateTimeSchema.nullable(),
  updatedAt: isoDateTimeSchema,
} as const;

function validateJobSummary(
  job: {
    jobStatus: JobStatus;
    postedAt: string | null;
    requestStartAt: string;
    requestEndAt: string;
  },
  context: z.RefinementCtx,
): void {
  if (Date.parse(job.requestEndAt) <= Date.parse(job.requestStartAt)) {
    context.addIssue({
      code: "custom",
      path: ["requestEndAt"],
      message: "Request end must follow request start",
    });
  }
  if ((job.jobStatus === "DRAFT") !== (job.postedAt === null)) {
    context.addIssue({
      code: "custom",
      path: ["postedAt"],
      message: "Publication time must match job status",
    });
  }
}

const fixedJobSummarySchema = z.strictObject({
  ...jobSummaryCommonFields,
  bookingMode: z.literal("FIXED_ACCEPT"),
  jobStatus: fixedJobStatusSchema,
  fixedPriceAmountMinor: fixedPriceSchema,
  startingPriceAmountMinor: z.null(),
});
const negotiableJobSummarySchema = z.strictObject({
  ...jobSummaryCommonFields,
  bookingMode: z.literal("NEGOTIABLE"),
  jobStatus: jobStatusSchema,
  fixedPriceAmountMinor: z.null(),
  startingPriceAmountMinor: fixedPriceSchema,
});
export const jobSummarySchema = z
  .discriminatedUnion("bookingMode", [fixedJobSummarySchema, negotiableJobSummarySchema])
  .superRefine(validateJobSummary);

export const jobPropertySnapshotSchema = z.strictObject({
  propertyId: jobIdSchema,
  propertyCode: z.string().min(1),
  propertyName: z.string().min(1),
  propertyType: propertyTypeSchema,
  bedroomCount: bedroomCountSchema,
  bathroomCount: bathroomCountSchema,
  roomCount: nullableRoomCountSchema,
  sizeSqft: z.number().int().positive().max(99_999_999).nullable(),
});

export const jobServiceAddressSnapshotSchema = z.strictObject({
  addressVersionId: jobIdSchema,
  line1: z.string().min(1),
  line2: z.string().nullable(),
  locality: z.string().min(1),
  regionCode: z.string().nullable(),
  postalCode: z.string().min(1),
  countryCode: z.string().regex(/^[A-Z]{2}$/u),
  timezoneRegion: z.string().min(1),
});

export const jobServiceSchema = z.strictObject({
  serviceId: jobIdSchema,
  serviceCode: z.string().min(1),
  serviceName: z.string().min(1),
  pricingUnit: jobPricingUnitSchema,
  quantity: z.number().int().min(1).max(100),
  notes: z.string().nullable(),
});

const jobStatusHistoryFields = {
  sequenceNo: z.number().int().min(1),
  reasonCode: z.string().min(1),
  changedAt: isoDateTimeSchema,
} as const;
const fixedJobStatusHistorySchema = z.strictObject({
  ...jobStatusHistoryFields,
  fromStatus: fixedJobStatusSchema.nullable(),
  toStatus: fixedJobStatusSchema,
});
const negotiableJobStatusHistorySchema = z.strictObject({
  ...jobStatusHistoryFields,
  fromStatus: jobStatusSchema.nullable(),
  toStatus: jobStatusSchema,
});
export const jobStatusHistorySchema = z.strictObject({
  ...jobStatusHistoryFields,
  fromStatus: jobStatusSchema.nullable(),
  toStatus: jobStatusSchema,
});

const jobDetailFields = {
  serviceAddressVersionId: jobIdSchema,
  bedroomCount: bedroomCountSchema,
  bathroomCount: bathroomCountSchema,
  roomCount: nullableRoomCountSchema,
  sizeSqft: z.number().int().positive().max(99_999_999).nullable(),
  specialInstructions: z.string().max(4_000).nullable(),
  needsLinen: z.boolean(),
  linenSetCount: z.number().int().min(0).max(9_999),
  needsTowels: z.boolean(),
  towelSetCount: z.number().int().min(0).max(9_999),
  propertySnapshot: jobPropertySnapshotSchema,
  serviceAddressSnapshot: jobServiceAddressSnapshotSchema,
  services: z.array(jobServiceSchema).min(1).max(100),
} as const;

const fixedJobDetailSchema = z.strictObject({
  ...jobSummaryCommonFields,
  bookingMode: z.literal("FIXED_ACCEPT"),
  jobStatus: fixedJobStatusSchema,
  fixedPriceAmountMinor: fixedPriceSchema,
  startingPriceAmountMinor: z.null(),
  ...jobDetailFields,
  statusHistory: z.array(fixedJobStatusHistorySchema),
});
const negotiableJobDetailSchema = z.strictObject({
  ...jobSummaryCommonFields,
  bookingMode: z.literal("NEGOTIABLE"),
  jobStatus: jobStatusSchema,
  fixedPriceAmountMinor: z.null(),
  startingPriceAmountMinor: fixedPriceSchema,
  ...jobDetailFields,
  statusHistory: z.array(negotiableJobStatusHistorySchema),
});

function validateJobDetail(
  job: z.infer<typeof fixedJobDetailSchema> | z.infer<typeof negotiableJobDetailSchema>,
  context: z.RefinementCtx,
): void {
  validateJobSummary(job, context);
  if (job.propertySnapshot.propertyId !== job.propertyId) {
    context.addIssue({
      code: "custom",
      path: ["propertySnapshot", "propertyId"],
      message: "Property snapshot must belong to the job",
    });
  }
  if (job.serviceAddressSnapshot.addressVersionId !== job.serviceAddressVersionId) {
    context.addIssue({
      code: "custom",
      path: ["serviceAddressSnapshot", "addressVersionId"],
      message: "Address snapshot must belong to the job",
    });
  }
  if (job.serviceAddressSnapshot.timezoneRegion !== job.timezoneRegion) {
    context.addIssue({
      code: "custom",
      path: ["serviceAddressSnapshot", "timezoneRegion"],
      message: "Address and job timezones must match",
    });
  }
  if (!job.needsLinen && job.linenSetCount !== 0) {
    context.addIssue({ code: "custom", path: ["linenSetCount"], message: "Invalid linen count" });
  }
  if (!job.needsTowels && job.towelSetCount !== 0) {
    context.addIssue({ code: "custom", path: ["towelSetCount"], message: "Invalid towel count" });
  }
  const serviceIds = new Set<string>();
  job.services.forEach(({ serviceId }, index) => {
    if (serviceIds.has(serviceId)) {
      context.addIssue({
        code: "custom",
        path: ["services", index, "serviceId"],
        message: "Duplicate service",
      });
    }
    serviceIds.add(serviceId);
  });
}

export const jobDetailSchema = z
  .discriminatedUnion("bookingMode", [fixedJobDetailSchema, negotiableJobDetailSchema])
  .superRefine(validateJobDetail);

export const jobListResponseSchema = z.strictObject({
  status: z.literal("ok"),
  jobs: z.array(jobSummarySchema),
});
export const jobResponseSchema = z.strictObject({
  status: z.literal("ok"),
  job: jobDetailSchema,
});
export const createJobResponseSchema = z.strictObject({
  status: z.enum(["created", "existing"]),
  job: jobDetailSchema,
});
export const updateJobResponseSchema = z.strictObject({
  status: z.enum(["updated", "existing"]),
  job: jobDetailSchema,
});
export const publishJobResponseSchema = z.strictObject({
  status: z.enum(["published", "existing"]),
  job: jobDetailSchema,
});

// Cancellation is a compact command acknowledgement. The store rereads the customer job
// projection before changing local state so commands do not duplicate the full read model.
export const jobCancellationAckSchema = z.strictObject({
  id: jobIdSchema,
  jobId: jobIdSchema,
  jobStatus: z.literal("CANCELLED"),
  jobVersionNo: versionNoSchema,
  cancelledAt: isoDateTimeSchema,
});
export const cancelJobResponseSchema = z.strictObject({
  status: z.enum(["cancelled", "existing"]),
  cancellation: jobCancellationAckSchema,
});

export type JobBookingMode = z.infer<typeof jobBookingModeSchema>;
export type CleaningType = z.infer<typeof cleaningTypeSchema>;
export type JobStatus = z.infer<typeof jobStatusSchema>;
export type JobServiceInput = z.infer<typeof jobServiceInputSchema>;
export type CreateJobInput = z.infer<typeof createJobInputSchema>;
export type UpdateJobInput = z.infer<typeof updateJobInputSchema>;
export type ReplaceJobServicesInput = z.infer<typeof replaceJobServicesInputSchema>;
export type PublishJobInput = z.infer<typeof publishJobInputSchema>;
export type CancelJobInput = z.infer<typeof cancelJobInputSchema>;
export type CancelJobResponse = z.infer<typeof cancelJobResponseSchema>;
export type JobSummary = z.infer<typeof jobSummarySchema>;
export type JobDetail = z.infer<typeof jobDetailSchema>;
